import { observer } from "mobx-react-lite";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import {
  getSidebarDefaultOpen,
  SidebarInset,
  SidebarProvider,
} from "@/components/ui-kit/base/sidebar";
import { cn } from "@/utils/shadcn-ui";

import { Spinner } from "../../components/ui-kit/spinner";
import { devTools } from "../../config/config";
import { hasAIChatAccess } from "../../constants/permissions";
import { ChatBubble } from "../../features/chat/chat-bubble";
import { DevTools } from "../../features/dev-tools/dev-tools";
import { patientProfileHeaderVisible } from "../../features/patient/profile-header-routes";
import { AppModel } from "../app-model";
import { Footer } from "../footer";
import { PasswordPrompt } from "../password-prompt";
import { BackButton } from "./back-button";
import { BreadcrumbsTitle } from "./breadcrumbs-title";
import { DocumentTitle } from "./document-title";
import { LayoutProvider } from "./layout-context";
import { LegacyHeader } from "./legacy/legacy-header";
import { LegacySidebar } from "./legacy/legacy-sidebar";
import { MainHeader } from "./main-header";
import { AppSidebar } from "./sidebar/app-sidebar";

export const MainLayout = observer(function MainLayout(props: {
  app: AppModel;
}) {
  const [headerActionsNode, setHeaderActionsNode] =
    useState<HTMLElement | null>(null);

  // The profile header already names the patient — `#123`, name, age/sex,
  // state — so the visible title row above it would only repeat that
  // identity. It is the *only* condition: one predicate, shared with the route
  // that renders the header, rather than a second list of routes to keep in
  // step.
  // The legacy layout predates this and keeps its title row everywhere.
  const pathname = useLocation().pathname;
  const showTitle =
    props.app.legacyLayout || !patientProfileHeaderVisible(pathname);

  const layoutContextValue = useMemo(
    () => ({ headerActionsNode }),
    [headerActionsNode],
  );

  const content = (
    <>
      <main className="grow mb-12">
        {/* The actions slot stays in both cases — six routes
            portal into it — but with no title beside it the row
            carries its own spacing, so a page that contributes no
            actions leaves an empty div and no vertical space at
            all. */}
        <div
          className={cn(
            "flex items-start justify-between",
            showTitle && "mb-9 mt-9",
          )}
        >
          {/* Visually hidden rather than removed where the header stands in
              for it: the page still needs a heading that names it, not only
              the patient, for anyone navigating by headings. */}
          <BreadcrumbsTitle
            className={cn("mb-0 mt-0", !showTitle && "sr-only")}
          />
          {/* Flex row, not a bare div: a route can put several
              controls in the header, and they must sit inline
              rather than stack. */}
          <div
            ref={setHeaderActionsNode}
            className={cn(
              "flex items-center gap-4",
              !showTitle && "ml-auto pt-4 pb-2 empty:hidden",
            )}
          />
        </div>

        <BackButton />

        {/* Catalog hooks (e.g. useSupportedDiagnoses) suspend until
            their reference data loads; this boundary keeps that loading
            out of leaf components across every page. */}
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      <Footer app={props.app} />
    </>
  );

  useEffect(() => {
    void props.app.seerlinqApi.assumeLoggedIn();
  }, [props.app.seerlinqApi]);

  return (
    <>
      {props.app.seerlinqApi.promptPassword && (
        <PasswordPrompt api={props.app.seerlinqApi} />
      )}

      {props.app.seerlinqApi.loggedIn ? (
        <LayoutProvider value={layoutContextValue}>
          <DocumentTitle app={props.app} />
          {props.app.legacyLayout ? (
            <>
              <LegacyHeader app={props.app} />
              <div className="grow flex">
                <LegacySidebar app={props.app} />
                <div className="grow flex flex-col pl-4 nav:pl-[60px] min-w-0 pr-4">
                  {content}
                </div>
              </div>
            </>
          ) : (
            <SidebarProvider defaultOpen={getSidebarDefaultOpen()}>
              {/* The header is a fixed-height top bar outside the sidebar/content
                  row, so the sidebar starts below it rather than running full
                  height beside it. `SidebarProvider`'s own root is a flex row,
                  so this single wrapper (header stacked over the row) is its
                  only child. */}
              <div className="flex min-h-svh w-full flex-col [--app-header-height:52px]">
                <MainHeader app={props.app} />
                <div className="flex grow">
                  <AppSidebar app={props.app} />
                  <SidebarInset>
                    <div className="grow flex flex-col min-w-0 px-4">
                      {content}
                    </div>
                  </SidebarInset>
                </div>
              </div>
            </SidebarProvider>
          )}
          {hasAIChatAccess(props.app.seerlinqApi.permissions) && (
            <ChatBubble api={props.app.seerlinqApi} />
          )}
          {devTools && (
            <DevTools
              open={props.app.devToolsOpen}
              onOpenChange={(open) => props.app.setDevToolsOpen(open)}
              api={props.app.seerlinqApi}
            />
          )}
        </LayoutProvider>
      ) : (
        <div className="grow flex items-center justify-center">
          <Spinner />
        </div>
      )}
    </>
  );
});
